// Turn resolution for tactical-zone play: applies each unit's order for the
// turn, advances every enemy once via tactical-enemy-ai.mjs's advanceEnemy()
// with a fixed per-turn game-minute delta, and resolves contact/extraction.
//
// This is the tactical-mode turn engine: one resolveTacticalTurn call advances
// one complete turn.

import { advanceAlongPathByMeters, advanceEnemy, ENEMY_CONTACT_METERS, ENEMY_STATES } from "./tactical-enemy-ai.mjs";
import { distanceInMeters } from "../road-graph.mjs";
import { facingBearingDegrees } from "./vision-cone.mjs";
import { sightLineBlocked } from "./line-of-sight.mjs";
import { distanceToNearestCrossing } from "./tactical-zone.mjs";
import { advanceWithTerrainCost, pathIndexForPosition } from "./tactical-movement.mjs";
import { rollSearchItems } from "./items.mjs";
import { MOOK_MAX_HEALTH, SURVIVOR_MELEE_DAMAGE, SURVIVOR_MAX_HEALTH } from "./combat-math.mjs";
import {
  THREAT_PER_DETECTION,
  THREAT_PER_REINFORCEMENT,
  THREAT_PER_SCAVENGE,
  THREAT_PER_TURN_TICK,
  THREAT_TURN_INTERVAL,
  rollSearchThreat,
} from "./threat.mjs";

export const TACTICAL_TURN_GAME_MINUTES = 1;
export const DEFAULT_MOVE_METERS_PER_TURN = 15;
// Reinforcements drawn in by threat patrol at the same speed as the zone's
// starting enemies (kept local to avoid a cycle with tactical-session.mjs).
export const THREAT_SPAWN_ENEMY_SPEED_METERS_PER_GAME_MINUTE = 18;
// A threat-spawned enemy won't be placed at a boundary crossing closer than this
// to any surviving unit, so a spawn can't reach and kill a unit next turn
// (enemy move + contact reach is well under this). Falls back to the farthest
// available crossing if every crossing is nearer than this.
export const THREAT_SPAWN_MIN_UNIT_DISTANCE_METERS = 45;
export const EXTRACTION_RANGE_METERS = 5;
export const INTERACT_RANGE_METERS = 5;
// How close to the POI entrance a survivor must get to slip inside.
export const POI_ENTER_RANGE_METERS = 6;
// How close a unit must get to an enemy to land a melee attack — same reach the
// enemy uses to make contact, so a strike and a retaliation share one range.
export const MELEE_RANGE_METERS = ENEMY_CONTACT_METERS;
// After everyone has moved, bodies of the same side shouldn't stack on one spot:
// any two closer than this get nudged apart (wall-aware). Roughly a marker's
// width, so a cluster reads as distinct dots without fighting a doorway funnel.
// Only same-side (enemy↔enemy, survivor↔survivor) — an enemy and a survivor are
// left to close to melee range.
export const ENEMY_SEPARATION_METERS = 1.6;
export const UNIT_SEPARATION_METERS = 1.6;
const SEPARATION_ITERATIONS = 4;
// Turns spent scavenging inside a POI before the supplies are secured.
export const SCAVENGE_TURNS = 1;
// Every survivor starts with four bag slots. Equipped gear can raise this per
// unit via unit.carryCapacity.
export const DEFAULT_CARRY_CAPACITY = 4;
// A recruited companion trails its recruiter. It moves a touch faster than a unit
// (and ignores off-street terrain cost) so it never lags behind, settling a short
// way back rather than stacking exactly on top of the unit. The precise on-screen
// spacing is enforced pixel-perfectly by the supply-run view (COMPANION_GAP_PX); these
// metres just keep the sim position trailing along the path.
export const COMPANION_MOVE_METERS_PER_TURN = DEFAULT_MOVE_METERS_PER_TURN + 6;
export const COMPANION_FOLLOW_GAP_METERS = 3;
// Where a freshly recruited companion appears: this far behind the recruiter
// (opposite its facing), so it reads as a distinct second body immediately
// instead of spawning directly on top of the unit that found it.
export const COMPANION_SPAWN_OFFSET_METERS = 4;
// Close enough to the recruiter's exit point to count as "made it out" — the
// companion is removed from the map once it reaches the exit its recruiter took.
export const COMPANION_EXIT_ARRIVE_METERS = 4;

function remainingCapacity(unit) {
  const capacity = unit.carryCapacity ?? DEFAULT_CARRY_CAPACITY;
  return Math.max(0, capacity - (unit.inventory || []).length);
}

// scatteredItems: pickup entities dropped around the zone (see
// items.mjs's rollScatteredItems), each { id, itemId, position }. A unit claims
// one with an "interact" order carrying that pickupId (see applyUnitOrder).
//
// buildingContents: the per-building scavenge pool (see building-scavenge.mjs),
// keyed by building id -> { contents: [itemId], promising, ransacked }. A unit
// scavenges a specific building via the legacy "tactical" order carrying that buildingId,
// pulling one item per turn from the shared pool (depleting it for re-visits /
// other units). Accepts a Map or a plain object. When absent, "tactical" orders
// fall back to the legacy single-POI roll (see applyScavengeOrder).
//
// survivorBuildings: buildings that harbour a recruitable wild survivor (see
// survivor-building.mjs), keyed by building id -> { buildingId, name,
// backgroundId }. A unit that reaches one via a "recruit" order recruits the
// survivor: a following companion spawns (state.companions) and the roster
// addition is staged (state.pendingRecruits), banked on run completion.
export function createTacticalState({ zoneId, units, enemies, scatteredItems, buildingContents, survivorBuildings, fieldObjects }) {
  const buildingEntries = buildingContents instanceof Map
    ? [...buildingContents.entries()]
    : Object.entries(buildingContents || {});
  const survivorList = survivorBuildings instanceof Map
    ? [...survivorBuildings.values()]
    : Array.isArray(survivorBuildings)
      ? survivorBuildings
      : Object.values(survivorBuildings || {});
  return {
    zoneId,
    turnNumber: 0,
    units: Object.fromEntries((units || []).map((unit) => [
      unit.id,
      {
        facingDegrees: 0,
        inventory: [],
        scavenged: false,
        order: null,
        status: "active",
        // Combat HP safety net: a unit built without health (older callers)
        // starts at full. createTacticalUnit already seeds this from the campaign
        // survivor, so its own value wins via the spread below.
        health: { current: SURVIVOR_MAX_HEALTH, max: SURVIVOR_MAX_HEALTH },
        ...unit,
      },
    ])),
    // Every enemy carries combat HP so a unit can fight back (mook default; an
    // enemy that already supplies health keeps its own via the spread).
    enemies: Object.fromEntries((enemies || []).map((enemy) => [
      enemy.id,
      { health: { current: MOOK_MAX_HEALTH, max: MOOK_MAX_HEALTH }, ...enemy },
    ])),
    // Destructible field objects (base-defense doors, later barricades): static
    // HP entities that never move or take orders. An attacker blocked by one
    // chips its HP until it breaches (hp 0), then passes. Keyed by id.
    fieldObjects: Object.fromEntries((fieldObjects || []).map((object) => [
      object.id,
      { hp: object.maxHp ?? object.hp ?? 0, breached: false, ...object },
    ])),
    scatteredItems: Object.fromEntries((scatteredItems || []).map((pickup) => [pickup.id, { ...pickup }])),
    buildingContents: Object.fromEntries(buildingEntries.map(([id, building]) => [
      id,
      { promising: Boolean(building.promising), ransacked: Boolean(building.ransacked), contents: [...(building.contents || [])] },
    ])),
    survivorBuildings: Object.fromEntries(survivorList.filter(Boolean).map((entry) => [
      String(entry.buildingId),
      { buildingId: String(entry.buildingId), name: entry.name, backgroundId: entry.backgroundId ?? null },
    ])),
    // Recruited survivors following the squad this run, keyed by companion id ->
    // { id, name, backgroundId, position, followUnitId }. Not selectable/targetable
    // (they never enter `units`). Banked to the roster at run end via pendingRecruits.
    companions: {},
    // Staged roster additions: { companionId, recruiterUnitId, name, backgroundId,
    // buildingId }. Applied on supply-run completion, but only for recruiters who survived.
    pendingRecruits: [],
    // Persistent close-combat intent, keyed by unit id -> { enemyId }. The tactical
    // view consumes this as an automatic next-turn attack while the enemy remains
    // in melee range.
    engagements: {},
    noiseEvents: [],
    // Accumulated supply-run threat (v4 threat system): noisy searches, being spotted,
    // and lingering add to it each turn. On extract it's applied to the POI +
    // the campaign-wide gauge (see applySupplyRunOutcome).
    threat: 0,
    extractedUnitIds: [],
    deadUnitIds: [],
    log: [],
  };
}

export function isTacticalSessionOver(tacticalState) {
  return Object.keys(tacticalState.units).length === 0;
}

// orders: { [unitId]: { type: "move"|"tactical"|"interact"|"extract"|"recruit", ... } }
// context: { zone, graph, routeTo, sightMeters?, visionConeDegrees? } — graph
// and routeTo are threaded straight through to advanceEnemy(), same contract
// as the continuous-world caller uses.
// context.freezeEnemies: resolve ONLY the given unit orders (movement, scavenge,
// supplies, extraction, recruits, scavenge-threat) without advancing enemies,
// generating detection/lingering threat, drawing reinforcements, or advancing
// the turn number. This is the "player phase" step used by the interactive supply run
// so a unit acts the instant it's ordered; the separate enemy phase is a plain
// resolveTacticalTurn(state, {}, context) call (no freeze). Headless callers
// never set it, so their all-at-once behavior is unchanged.
export function resolveTacticalTurn(tacticalState, orders, context) {
  const freezeEnemies = Boolean(context.freezeEnemies);
  const gameMinutes = (tacticalState.turnNumber + 1) * TACTICAL_TURN_GAME_MINUTES;
  const stepContext = { ...context, gameMinutes };

  const units = { ...tacticalState.units };
  const noiseEvents = [];
  const log = [];
  const engagements = { ...(tacticalState.engagements || {}) };
  const extractedUnitIds = [...tacticalState.extractedUnitIds];
  const deadUnitIds = [...tacticalState.deadUnitIds];

  // Mutable copy of the shared building pool (fresh contents arrays) so a
  // unit's scavenge this turn depletes it without mutating the prior state.
  const buildingContents = Object.fromEntries(
    Object.entries(tacticalState.buildingContents || {}).map(([id, building]) => [id, { ...building, contents: [...building.contents] }]),
  );

  for (const [unitId, order] of Object.entries(orders || {})) {
    const unit = units[unitId];
    if (!unit || !order) continue;
    delete engagements[unitId];
    units[unitId] = applyUnitOrder(unit, order, stepContext, log, buildingContents);
  }

  // Resolve scattered-item claims: a unit that reached a pickup this turn
  // (see the "interact" branch of applyUnitOrder) actually collects it here,
  // once, against the shared pickup pool — first order processed wins if two
  // units somehow reach the same pickup on the same turn.
  const scatteredItems = { ...tacticalState.scatteredItems };
  for (const effect of log.filter((entry) => entry.type === "pickup-reached")) {
    const pickup = scatteredItems[effect.pickupId];
    const unit = units[effect.unitId];
    if (!pickup || !unit) continue;
    if (remainingCapacity(unit) <= 0) {
      // Full up — leave it on the ground rather than losing it; the unit can
      // come back for it after dropping/banking something.
      log.push({ type: "pickup-full", unitId: effect.unitId, pickupId: effect.pickupId });
      continue;
    }
    delete scatteredItems[effect.pickupId];
    units[effect.unitId] = { ...unit, inventory: [...(unit.inventory || []), pickup.itemId] };
    log.push({ type: "picked-up", unitId: effect.unitId, itemId: pickup.itemId, pickupId: effect.pickupId, coordinate: [...pickup.position] });
  }

  // Items dropped this turn (see the "drop" order in applyUnitOrder) land on
  // the ground as fresh scattered pickups, so they can be reclaimed — e.g.
  // drop a low-value item to free a slot, grab it again later if you have room.
  for (const effect of log.filter((entry) => entry.type === "dropped-item")) {
    const dropId = `drop:${effect.unitId}:${tacticalState.turnNumber + 1}:${effect.itemId}`;
    scatteredItems[dropId] = { id: dropId, itemId: effect.itemId, position: [...effect.coordinate] };
  }

  const targets = Object.values(units)
    // A unit scavenging inside a POI is hidden — enemies can't target it.
    .filter((unit) => !deadUnitIds.includes(unit.id) && !extractedUnitIds.includes(unit.id) && unit.status !== "scavenging")
    .map((unit) => ({ id: unit.id, kind: "survivor", coordinate: unit.position }));

  const enemies = {};
  let detections = 0;
  if (freezeEnemies) {
    // Player phase: enemies hold their positions/behavior; they act in the
    // separate enemy phase.
    for (const [enemyId, enemy] of Object.entries(tacticalState.enemies)) enemies[enemyId] = enemy;
  } else {
    for (const [enemyId, enemy] of Object.entries(tacticalState.enemies)) {
      const { enemy: advanced, effects } = advanceEnemy(enemy, { ...stepContext, targets, noises: noiseEvents, fieldObjects: tacticalState.fieldObjects });
      enemies[enemyId] = advanced;
      for (const effect of effects) log.push({ ...effect, enemyId });
      // Being spotted raises threat: count each enemy that newly locks onto a
      // survivor this turn (enters pursuit from any calmer state — it saw or
      // caught someone). An enemy already pursuing doesn't re-charge threat.
      if (enemy.behavior !== ENEMY_STATES.pursue && advanced.behavior === ENEMY_STATES.pursue) detections += 1;
    }
  }

  // Attackers chipping field objects (doors): an enemy blocked by an intact door
  // on its route emits an "attack-object" effect (see the raider archetype). Apply
  // the damage here against a mutable copy; a door at 0 HP breaches (becomes
  // passable — the nav grid gates it via doorPassable/breachedDoorEdges).
  const fieldObjects = Object.fromEntries(
    Object.entries(tacticalState.fieldObjects || {}).map(([id, object]) => [id, { ...object }]),
  );
  for (const effect of log) {
    if (effect.type !== "attack-object") continue;
    const object = fieldObjects[effect.objectId];
    if (!object || object.breached) continue;
    const hpAfter = Math.max(0, (object.hp ?? 0) - (effect.damage ?? 1));
    fieldObjects[effect.objectId] = { ...object, hp: hpAfter, breached: hpAfter <= 0 };
    if (hpAfter <= 0) log.push({ type: "object-breached", objectId: effect.objectId, edgeKey: object.edgeKey ?? null, enemyId: effect.enemyId, coordinate: effect.coordinate ? [...effect.coordinate] : null });
  }

  // Player strikes: a unit that reached melee range of its target enemy this turn
  // (an "attack" order → "attack-reached") lands a proactive hit on it. This runs
  // in the player phase; the enemy's retaliation comes on the separate enemy
  // phase below. Applied against the shared enemy pool, so a re-issued attack on
  // an already-dead enemy is a harmless no-op.
  for (const effect of log) {
    if (effect.type !== "attack-reached") continue;
    const enemy = enemies[effect.enemyId];
    if (!enemy) continue;
    const healthAfter = Math.max(0, (enemy.health?.current ?? MOOK_MAX_HEALTH) - SURVIVOR_MELEE_DAMAGE);
    if (healthAfter <= 0) {
      delete enemies[effect.enemyId];
      delete engagements[effect.unitId];
      log.push({ type: "enemy-killed", enemyId: effect.enemyId, unitId: effect.unitId, byPlayer: true, coordinate: [...effect.coordinate] });
    } else {
      // A struck-but-surviving enemy turns on its attacker: it locks into pursuit
      // and faces the unit, so it retaliates on the coming enemy phase (this is
      // the "you hit, then they hit back" beat). Getting the kill in first is the
      // only way to avoid that return blow.
      enemies[effect.enemyId] = {
        ...enemy,
        health: { current: healthAfter, max: enemy.health?.max ?? MOOK_MAX_HEALTH },
        behavior: ENEMY_STATES.pursue,
        facingDegrees: facingBearingDegrees(enemy.position, effect.coordinate),
        memory: { ...(enemy.memory || {}), lastKnown: { coordinate: [...effect.coordinate], kind: "contact", atGameMinute: gameMinutes, sourceId: effect.unitId } },
      };
      engagements[effect.unitId] = { enemyId: effect.enemyId };
      log.push({ type: "enemy-damaged", enemyId: effect.enemyId, unitId: effect.unitId, healthAfter, coordinate: [...effect.coordinate] });
      log.push({ type: "unit-engaged", unitId: effect.unitId, enemyId: effect.enemyId, source: "attack", coordinate: [...effect.coordinate] });
    }
  }

  // Enemy retaliation: on the enemy phase, every enemy in contact with a survivor
  // lands a hit. There is NO survivor counter here — a survivor's damage comes
  // only from an explicit attack order on the player's own turn, so standing next
  // to an enemy costs HP each turn until you strike back or break away. Group
  // contacts by survivor and assign each enemy to its nearest so a shared enemy
  // strikes once (and a second survivor moving in splits the attackers).
  const contactsBySurvivor = new Map(); // unitId -> [enemyId, ...]
  for (const effect of log) {
    if (effect.type !== "contact" || effect.targetKind !== "survivor") continue;
    const unit = units[effect.targetId];
    if (!unit || deadUnitIds.includes(unit.id) || extractedUnitIds.includes(unit.id)) continue;
    if (!enemies[effect.enemyId]) continue; // enemy already gone this turn
    if (!contactsBySurvivor.has(unit.id)) contactsBySurvivor.set(unit.id, []);
    const list = contactsBySurvivor.get(unit.id);
    if (!list.includes(effect.enemyId)) list.push(effect.enemyId);
  }

  const enemyAssignedTo = new Map(); // enemyId -> unitId
  for (const [unitId, enemyIds] of contactsBySurvivor) {
    for (const enemyId of enemyIds) {
      const held = enemyAssignedTo.get(enemyId);
      if (!held) { enemyAssignedTo.set(enemyId, unitId); continue; }
      const enemy = enemies[enemyId];
      const dHeld = distanceInMeters(enemy.position, units[held].position);
      const dNew = distanceInMeters(enemy.position, units[unitId].position);
      if (dNew < dHeld) enemyAssignedTo.set(enemyId, unitId);
    }
  }

  for (const [unitId, enemyIds] of contactsBySurvivor) {
    const unit = units[unitId];
    if (!unit || deadUnitIds.includes(unit.id) || extractedUnitIds.includes(unit.id)) continue;
    const hits = enemyIds.filter((enemyId) => enemies[enemyId] && enemyAssignedTo.get(enemyId) === unitId).length;
    if (!hits) continue;

    const damage = hits * SURVIVOR_MELEE_DAMAGE;
    const healthAfter = Math.max(0, unit.health.current - damage);
    units[unitId] = { ...unit, health: { current: healthAfter, max: unit.health.max } };
    if (healthAfter <= 0) {
      deadUnitIds.push(unit.id);
      delete engagements[unit.id];
      log.push({ type: "unit-lost", unitId: unit.id, survivorId: unit.survivorId, coordinate: [...unit.position], inventory: [...(unit.inventory || [])] });
    } else {
      const engagedEnemyId = enemyIds
        .filter((enemyId) => enemies[enemyId] && enemyAssignedTo.get(enemyId) === unitId)
        .sort((a, b) => distanceInMeters(enemies[a].position, unit.position) - distanceInMeters(enemies[b].position, unit.position))[0];
      if (engagedEnemyId) {
        engagements[unit.id] = { enemyId: engagedEnemyId };
        log.push({ type: "unit-engaged", unitId: unit.id, enemyId: engagedEnemyId, source: "contact", coordinate: [...unit.position] });
      }
      log.push({ type: "unit-damaged", unitId: unit.id, survivorId: unit.survivorId, damage, healthAfter, coordinate: [...unit.position] });
    }
  }

  for (const unit of Object.values(units)) {
    if (deadUnitIds.includes(unit.id) || extractedUnitIds.includes(unit.id)) continue;
    const zone = context.zone;
    const reachedCrossing = zone && distanceToNearestCrossing(unit.position, zone) <= EXTRACTION_RANGE_METERS;
    // Extraction is order-driven: a unit with an extract order leaves once it
    // has reached a boundary crossing (headless auto-run) or is standing on
    // the extraction band (the green exit-lane streets just outside the supply-run
    // circle; the interactive supply run stages the extract order from the "Extract
    // at end of turn?" confirm popup). The band trigger is opt-in via context.
    const inExtractionBand = context.autoExtractAtBoundary && zone?.centerCoordinate
      && distanceInMeters(unit.position, zone.centerCoordinate) >= zone.radiusMeters;
    if (unit.order?.type === "extract" && (reachedCrossing || inExtractionBand)) {
      extractedUnitIds.push(unit.id);
      delete engagements[unit.id];
      log.push({
        type: "unit-extracted",
        unitId: unit.id,
        survivorId: unit.survivorId,
        inventory: [...(unit.inventory || [])],
        // Combat HP the survivor walks out with, so the campaign can persist any
        // wounds taken in the supply run (they recover 1 HP/day back at base).
        health: unit.health ? { ...unit.health } : undefined,
        // The loadout the unit walks out with (it may have equipped gear found
        // in the supply run) so the survivor keeps it back at base.
        equipmentItemIds: unit.equipmentItemIds ? [...unit.equipmentItemIds] : undefined,
        coordinate: [...unit.position],
      });
    }
  }

  const survivingUnits = { ...units };
  for (const id of [...deadUnitIds, ...extractedUnitIds]) delete survivingUnits[id];

  // --- Recruits & companions (survivor recruitment) ------------------------
  // A unit that reached a survivor building this turn recruits its survivor: a
  // following companion spawns and the roster addition is staged. Companions
  // then trail their recruiter; if that recruiter died this turn the recruit is
  // lost (the "lost if recruiter dies" rule), if it extracted the companion just
  // holds position (the recruit is already banked for run end).
  const survivorBuildings = { ...(tacticalState.survivorBuildings || {}) };
  const companions = {};
  for (const [id, companion] of Object.entries(tacticalState.companions || {})) {
    companions[id] = { ...companion, position: [...companion.position] };
  }
  let pendingRecruits = [...(tacticalState.pendingRecruits || [])];

  for (const effect of log.filter((entry) => entry.type === "recruit-reached")) {
    const descriptor = survivorBuildings[effect.buildingId];
    if (!descriptor) continue; // already recruited (idempotent)
    delete survivorBuildings[effect.buildingId];
    const companionId = `companion:${effect.buildingId}`;
    // Spawn a short way behind the recruiter (opposite its facing) so the two
    // don't overlap at the moment of recruitment.
    const recruiter = units[effect.unitId];
    const spawnPosition = recruiter
      ? offsetMetersAlongAngle(effect.coordinate, (recruiter.facingDegrees ?? 0) + 180, COMPANION_SPAWN_OFFSET_METERS)
      : [...effect.coordinate];
    companions[companionId] = {
      id: companionId,
      name: descriptor.name,
      backgroundId: descriptor.backgroundId ?? null,
      position: spawnPosition,
      facingDegrees: recruiter?.facingDegrees ?? 0,
      followUnitId: effect.unitId,
    };
    pendingRecruits.push({
      companionId,
      recruiterUnitId: effect.unitId,
      name: descriptor.name,
      backgroundId: descriptor.backgroundId ?? null,
      buildingId: effect.buildingId,
    });
    log.push({ type: "survivor-recruited", unitId: effect.unitId, companionId, name: descriptor.name, coordinate: [...effect.coordinate] });
  }

  for (const [id, companion] of Object.entries(companions)) {
    if (deadUnitIds.includes(companion.followUnitId)) {
      delete companions[id];
      pendingRecruits = pendingRecruits.filter((recruit) => recruit.companionId !== id);
      log.push({ type: "companion-lost", companionId: id, name: companion.name });
      continue;
    }
    // Recruiter left the zone: the companion heads for the same exit and slips
    // out too (the recruit is already banked). Capture the exit once from the
    // recruiter's final position — still available in `units` on the extract
    // turn (only survivingUnits drops it) — then walk to it and leave.
    if (extractedUnitIds.includes(companion.followUnitId)) {
      if (!companion.exitTarget) {
        const exiter = units[companion.followUnitId];
        companion.exitTarget = exiter ? [...exiter.position] : [...companion.position];
      }
      advanceCompanionTowardPoint(companion, companion.exitTarget, gameMinutes, 0);
      if (distanceInMeters(companion.position, companion.exitTarget) <= COMPANION_EXIT_ARRIVE_METERS) {
        delete companions[id];
        log.push({ type: "companion-extracted", companionId: id, name: companion.name, coordinate: [...companion.position] });
      }
      continue;
    }
    const leader = survivingUnits[companion.followUnitId];
    if (leader) advanceCompanionTowardPoint(companion, leader.position, gameMinutes, COMPANION_FOLLOW_GAP_METERS);
  }

  // --- Threat generation (v4 threat system) --------------------------------
  // Sum this turn's threat from three sources and log each gain so the supply run
  // chrome can pulse/announce it. The rng (context.rng, Math.random in-app)
  // makes searches probabilistic; tests inject a deterministic stub.
  const rng = context.rng ?? Math.random;
  // The player phase doesn't advance the turn (units act during a turn; the
  // enemy phase closes it).
  const newTurnNumber = freezeEnemies ? tacticalState.turnNumber : tacticalState.turnNumber + 1;
  let threatGained = 0;
  const noteThreat = (source, amount) => {
    threatGained += amount;
    log.push({ type: "threat", source, amount });
  };
  // 1. Searching is noisy: each item pulled from a building has a chance to
  //    generate threat (per-building "scavenged-item" + legacy "scavenged").
  //    A Locksmith on the acting unit skips the roll entirely (V4
  //    profession-unlock system, plans/i-think-i-want-rosy-stream.md) — locked
  //    containers just never trip anything for them.
  for (const effect of log) {
    const isLocksmith = units[effect.unitId]?.professionCategory === "locksmith";
    if (effect.type === "scavenged-item") {
      if (!isLocksmith && rollSearchThreat(rng)) noteThreat("scavenge", THREAT_PER_SCAVENGE);
    } else if (effect.type === "scavenged") {
      for (let i = 0; i < (effect.items?.length ?? 0); i += 1) {
        if (!isLocksmith && rollSearchThreat(rng)) noteThreat("scavenge", THREAT_PER_SCAVENGE);
      }
    }
  }
  // 2. Being spotted.
  for (let i = 0; i < detections; i += 1) noteThreat("spotted", THREAT_PER_DETECTION);
  // 3. Lingering — every Nth turn costs threat no matter what (enemy phase only;
  //    the player phase doesn't close a turn).
  if (!freezeEnemies && newTurnNumber % THREAT_TURN_INTERVAL === 0) noteThreat("lingering", THREAT_PER_TURN_TICK);

  // Rising threat draws reinforcements: one new patrol per THREAT_PER_REINFORCEMENT
  // points of accumulated threat *crossed* this turn (so a slow trickle of noise
  // ramps pressure gradually, not one enemy per tick). They enter at a road
  // crossing on the zone boundary, kept away from the squad so nobody gets
  // spawn-killed, and only start acting next turn. Opt-in via context (the
  // interactive supply run view enables it; the headless auto-run stays deterministic).
  const priorThreat = tacticalState.threat ?? 0;
  const reinforcements = (context.reinforceOnThreat && !freezeEnemies)
    ? Math.floor((priorThreat + threatGained) / THREAT_PER_REINFORCEMENT) - Math.floor(priorThreat / THREAT_PER_REINFORCEMENT)
    : 0;
  const spawnAvoid = [];
  for (let i = 0; i < reinforcements; i += 1) {
    const spawned = spawnThreatEnemy(context.zone, Object.values(survivingUnits), newTurnNumber, i, spawnAvoid);
    if (!spawned) break;
    enemies[spawned.id] = spawned;
    spawnAvoid.push([...spawned.position]);
    log.push({ type: "enemy-spawned", enemyId: spawned.id, coordinate: [...spawned.position], reason: "threat" });
  }

  for (const [unitId, engagement] of Object.entries(engagements)) {
    if (!survivingUnits[unitId] || !enemies[engagement.enemyId]) delete engagements[unitId];
  }

  // Keep same-side bodies from stacking on one spot (enemies only when they moved
  // this turn — the frozen player phase leaves the input enemy objects untouched).
  const separatedEnemies = freezeEnemies ? enemies : separateEntities(enemies, ENEMY_SEPARATION_METERS, context.occluders);
  const separatedUnits = separateEntities(survivingUnits, UNIT_SEPARATION_METERS, context.occluders);

  return {
    ...tacticalState,
    turnNumber: newTurnNumber,
    units: separatedUnits,
    enemies: separatedEnemies,
    fieldObjects,
    scatteredItems,
    buildingContents,
    survivorBuildings,
    companions,
    pendingRecruits,
    engagements,
    noiseEvents,
    threat: (tacticalState.threat ?? 0) + threatGained,
    extractedUnitIds,
    deadUnitIds,
    log: [...tacticalState.log, ...log],
  };
}

// Build a reinforcement enemy at a zone boundary crossing (a road entrance to
// the circle). Picks the crossing that maximises the minimum distance to the
// squad — and to any crossing already used this turn — so simultaneous spawns
// spread out and none lands next to a unit. Returns null if the zone exposes no
// crossings. The enemy patrols inward along its crossing's corridor.
function spawnThreatEnemy(zone, units, turnNumber, index, avoidCoordinates) {
  const crossings = zone?.boundaryCrossings ?? [];
  if (!crossings.length) return null;

  let best = null;
  for (const crossing of crossings) {
    const coordinate = crossing.crossingCoordinate;
    if (!Array.isArray(coordinate)) continue;
    const minUnitDistance = units.length
      ? Math.min(...units.map((unit) => distanceInMeters(coordinate, unit.position)))
      : Infinity;
    const minAvoidDistance = avoidCoordinates.length
      ? Math.min(...avoidCoordinates.map((used) => distanceInMeters(coordinate, used)))
      : Infinity;
    // Prefer crossings that clear the safe radius from units; among those, the
    // one most separated from units and other fresh spawns.
    const safe = minUnitDistance >= THREAT_SPAWN_MIN_UNIT_DISTANCE_METERS;
    const score = Math.min(minUnitDistance, minAvoidDistance);
    if (!best || (safe && !best.safe) || (safe === best.safe && score > best.score)) {
      best = { crossing, score, safe };
    }
  }
  if (!best) return null;

  const { crossing } = best;
  const corridor = zone.corridors?.get?.(crossing.corridorId);
  const path = corridor
    ? corridor.coordinates.map((coordinate) => [...coordinate])
    : [[...crossing.crossingCoordinate], [...(zone.centerCoordinate ?? crossing.crossingCoordinate)]];
  return {
    id: `enemy:threat:${turnNumber}:${index}`,
    behavior: ENEMY_STATES.wander,
    health: { current: MOOK_MAX_HEALTH, max: MOOK_MAX_HEALTH },
    position: [...crossing.crossingCoordinate],
    path,
    pathIndex: 0,
    pathCorridorId: crossing.corridorId,
    targetNodeId: corridor?.endNodeId ?? null,
    speedMetersPerGameMinute: THREAT_SPAWN_ENEMY_SPEED_METERS_PER_GAME_MINUTE,
    lastAdvancedAtGameMinute: 0,
    anchorCoordinate: [...crossing.crossingCoordinate],
    facingDegrees: 0,
  };
}

function applyUnitOrder(unit, order, stepContext, log, buildingContents) {
  let next = { ...unit, order };

  // A unit scavenging inside a building is committed until it finishes,
  // whatever order it's been handed — it can't be pulled out mid-scavenge,
  // and can't burn items on a turn it isn't actually acting.
  if (unit.status === "scavenging") return applyScavengeOrder(unit, next, order, stepContext, log, 0, buildingContents);

  const moveBonus = 0;

  // Drop one carried item to the ground (a full turn's action) to free a slot
  // for food / higher-value supplies under pressure. It becomes a re-pickupable
  // scattered pickup (added to the shared pool in resolveTacticalTurn).
  if (order.type === "drop" && order.itemId) {
    const inventory = [...(next.inventory || [])];
    const index = inventory.indexOf(order.itemId);
    if (index !== -1) {
      inventory.splice(index, 1);
      next.inventory = inventory;
      log.push({ type: "dropped-item", unitId: unit.id, itemId: order.itemId, coordinate: [...next.position] });
    }
    return next;
  }

  if (order.type === "scavenge") return applyScavengeOrder(unit, next, order, stepContext, log, moveBonus, buildingContents);

  // Walk to a survivor building's doorstep and, on arrival, log "recruit-reached".
  // The recruit itself (spawning the following companion + staging the roster
  // addition) resolves in resolveTacticalTurn against the shared survivorBuildings
  // pool, so re-reaching an already-recruited building is a harmless no-op.
  if (order.type === "recruit") {
    const path = pathFromOrder(unit, order);
    const entrance = Array.isArray(order.target) ? order.target : (path ? path[path.length - 1] : null);
    if (path) advanceUnitAlongPath(next, unit, path, stepContext, moveBonus);
    if (entrance && distanceInMeters(next.position, entrance) <= POI_ENTER_RANGE_METERS) {
      log.push({ type: "recruit-reached", unitId: unit.id, buildingId: order.buildingId, coordinate: [...next.position] });
    }
    return next;
  }

  // Attack a specific enemy: close in like a move, and once within melee range
  // stage the strike (logged "attack-reached"). resolveTacticalTurn applies the
  // damage against the shared enemy pool, so this is the player's proactive hit;
  // the enemy's retaliation comes on the separate enemy phase. `target` is the
  // enemy's position at order time (enemies hold during the player phase).
  if (order.type === "attack") {
    const path = pathFromOrder(unit, order);
    // A held attack (base defense) never closes distance — the survivor stands its
    // ground at the opening and only strikes an enemy that comes into reach, so it
    // can't be dragged out of the building chasing a raider in the open.
    if (path && !order.hold) advanceUnitAlongPath(next, unit, path, stepContext, moveBonus);
    const target = Array.isArray(order.target) ? order.target : (path ? path[path.length - 1] : null);
    const meleeRange = stepContext.meleeRangeMeters ?? MELEE_RANGE_METERS;
    // Range AND line of sight, but only a solid WALL blocks the strike — never a
    // door. A defender must be able to hit a raider chipping their door, so
    // `strikeOccluders` carries walls only (doors excluded); you still can't stab
    // through a blank wall. A raid passes none, so its strikes are pure range.
    const inReach = target && distanceInMeters(next.position, target) <= meleeRange;
    const seen = inReach && !(stepContext.strikeOccluders && sightLineBlocked(next.position, target, stepContext.strikeOccluders));
    if (seen) {
      // Face the enemy for the strike, then log it.
      next.facingDegrees = facingBearingDegrees(next.position, target);
      log.push({ type: "attack-reached", unitId: unit.id, enemyId: order.enemyId, coordinate: [...next.position] });
    }
    return next;
  }

  if (order.type === "move" || order.type === "extract") {
    const path = pathFromOrder(unit, order);
    if (path) advanceUnitAlongPath(next, unit, path, stepContext, moveBonus);
  }

  // Walk to a scattered-item pickup (same multi-turn approach-and-arrive
  // pattern as a supply run's entrance) and claim it once in range. Reaching range
  // only stages the claim (logged as "pickup-reached") — resolveTacticalTurn()
  // actually grants the item, since the shared pickup pool lives outside any
  // single unit's order.
  if (order.type === "interact" && Array.isArray(order.target) && order.pickupId) {
    const path = pathFromOrder(unit, order);
    if (path) advanceUnitAlongPath(next, unit, path, stepContext, moveBonus);
    if (distanceInMeters(next.position, order.target) <= INTERACT_RANGE_METERS) {
      log.push({ type: "pickup-reached", unitId: unit.id, pickupId: order.pickupId, coordinate: [...next.position] });
    }
  }

  return next;
}

// Move a recruited companion one turn toward a point (its leader, or the exit it
// is following its recruiter to). Straight-line and terrain-free (companions are
// cosmetic escorts, not searchable units), stopping `gapMeters` short so it trails behind
// rather than overlapping. The on-screen spacing is finalized pixel-perfectly by
// the supply-run view; this keeps the sim position trailing along the path.
function advanceCompanionTowardPoint(companion, target, gameMinutes, gapMeters) {
  if (distanceInMeters(companion.position, target) <= gapMeters) return;
  const previousPosition = [...companion.position];
  const path = [[...companion.position], [...target]];
  const moved = advanceAlongPathByMeters({ position: companion.position, path, pathIndex: 0 }, COMPANION_MOVE_METERS_PER_TURN, gameMinutes);
  companion.position = moved.position;
  if (distanceInMeters(previousPosition, companion.position) > 0.1) {
    companion.facingDegrees = facingBearingDegrees(previousPosition, companion.position);
  }
}

// Offset a [lon, lat] by `meters` along a facing angle (the atan2(north, east)
// convention facingBearingDegrees returns: 0 = east, 90 = north).
function offsetMetersAlongAngle(coord, angleDegrees, meters) {
  const rad = (angleDegrees * Math.PI) / 180;
  const east = Math.cos(rad) * meters;
  const north = Math.sin(rad) * meters;
  const metersPerLon = Math.cos((coord[1] * Math.PI) / 180) * 111320;
  return [coord[0] + east / (metersPerLon || 1), coord[1] + north / 111320];
}

// De-overlap a set of same-side entities: a short relaxation that nudges any two
// bodies closer than `minGapMeters` apart, split evenly along the line between
// them. Wall-aware — a push that would cross an occluder (a wall/door in a
// defense, a building in a supply run) is skipped, so nobody is shoved through a
// wall and a doorway funnel just stays tight. Returns a new map (unmoved entities
// keep their identity); the input is never mutated.
export function separateEntities(entityMap, minGapMeters, occluders) {
  const ids = Object.keys(entityMap);
  if (ids.length < 2) return entityMap;
  const pos = {};
  for (const id of ids) pos[id] = [...entityMap[id].position];
  let anyMoved = false;
  for (let iter = 0; iter < SEPARATION_ITERATIONS; iter += 1) {
    let moved = false;
    for (let i = 0; i < ids.length; i += 1) {
      for (let j = i + 1; j < ids.length; j += 1) {
        const a = pos[ids[i]];
        const b = pos[ids[j]];
        const d = distanceInMeters(a, b);
        if (d >= minGapMeters) continue;
        // Coincident bodies get a deterministic split direction from their ids.
        const angle = d < 1e-6 ? separationSplitAngle(ids[i], ids[j]) : facingBearingDegrees(b, a);
        const push = (minGapMeters - d) / 2 + 1e-3;
        const aNext = offsetMetersAlongAngle(a, angle, push);
        const bNext = offsetMetersAlongAngle(b, angle + 180, push);
        if (!(occluders && sightLineBlocked(a, aNext, occluders))) { pos[ids[i]] = aNext; moved = true; }
        if (!(occluders && sightLineBlocked(b, bNext, occluders))) { pos[ids[j]] = bNext; moved = true; }
      }
    }
    if (moved) anyMoved = true; else break;
  }
  if (!anyMoved) return entityMap;
  const out = {};
  for (const id of ids) {
    const p = pos[id];
    const orig = entityMap[id].position;
    out[id] = p[0] === orig[0] && p[1] === orig[1] ? entityMap[id] : { ...entityMap[id], position: p };
  }
  return out;
}

// A stable pseudo-random bearing for two exactly-coincident bodies, so they
// split in a consistent direction instead of jittering.
function separationSplitAngle(idA, idB) {
  let hash = 0;
  const string = `${idA}|${idB}`;
  for (let i = 0; i < string.length; i += 1) hash = (hash * 31 + string.charCodeAt(i)) >>> 0;
  return hash % 360;
}

function pathFromOrder(unit, order) {
  if (Array.isArray(order.path) && order.path.length >= 2) return order.path;
  if (Array.isArray(order.target)) return [unit.position, order.target];
  return null;
}

// Advance a unit one turn's worth along a path, updating position, pathIndex,
// and facing on `next` (mutated). Persistent multi-point paths resume from the
// segment the unit is actually on. With terrain in context, off-street travel
// costs the multiplier; without it (plain unit tests), uniform speed.
function advanceUnitAlongPath(next, unit, path, stepContext, moveBonus = 0) {
  const moveBudget = (unit.moveMetersPerTurn ?? DEFAULT_MOVE_METERS_PER_TURN) + moveBonus;
  const previousPosition = [...unit.position];
  const startIndex = pathIndexForPosition(path, unit.position);
  const moved = stepContext.terrain
    ? advanceWithTerrainCost({ position: unit.position, pathIndex: startIndex }, path, moveBudget, stepContext.terrain)
    : advanceAlongPathByMeters({ position: unit.position, path, pathIndex: startIndex }, moveBudget, stepContext.gameMinutes);
  next.position = moved.position;
  next.pathIndex = moved.pathIndex;
  if (distanceInMeters(previousPosition, next.position) > 0.1) {
    next.facingDegrees = facingBearingDegrees(previousPosition, next.position);
  }
}

// Scavenge a building: approach its doorstep, slip inside, and pull supplies from
// the building's shared contents pool — one item per turn — then step back out
// the way you came in. "grab-one" leaves after a single item; "fill" keeps
// pulling until the bag is full or the building is empty (so lingering for a
// full haul is real exposure-by-commitment). Falls back to the legacy single-
// POI roll when the order carries no building (see applyLegacyScavengeOrder).
function applyScavengeOrder(unit, next, order, stepContext, log, moveBonus = 0, buildingContents = {}) {
  const buildingId = unit.scavengeBuildingId ?? order.buildingId ?? null;
  const pool = buildingId ? buildingContents[buildingId] : null;
  if (!pool) return applyLegacyScavengeOrder(unit, next, order, stepContext, log, moveBonus);

  if (unit.status === "scavenging") {
    const mode = unit.scavengeMode ?? "grab-one";
    const capacity = unit.carryCapacity ?? DEFAULT_CARRY_CAPACITY;
    const inventory = [...(unit.inventory || [])];

    // Pull one item this turn, if there's room and anything left.
    let pulledItemId = null;
    if (inventory.length < capacity && pool.contents.length > 0) {
      pulledItemId = pool.contents.shift();
      inventory.push(pulledItemId);
      log.push({ type: "scavenged-item", unitId: unit.id, buildingId, itemId: pulledItemId });
    }

    const bagFull = inventory.length >= capacity;
    const buildingEmpty = pool.contents.length === 0;
    const done = mode === "grab-one" || bagFull || buildingEmpty || pulledItemId === null;
    if (!done) return { ...next, inventory, status: "scavenging" };

    // Finished: step back out to where we entered.
    const exit = Array.isArray(unit.entryCoordinate) ? [...unit.entryCoordinate] : [...unit.position];
    log.push({ type: "scavenge-finished", unitId: unit.id, buildingId, coordinate: [...exit], reason: bagFull ? "bag-full" : buildingEmpty ? "building-empty" : "grabbed" });
    return {
      ...next,
      inventory,
      status: "active",
      position: exit,
      entryCoordinate: null,
      scavengeBuildingId: null,
      scavengeMode: null,
      order: null,
    };
  }

  // Approaching the building's doorstep.
  const path = pathFromOrder(unit, order);
  const entrance = Array.isArray(order.target) ? order.target : (path ? path[path.length - 1] : null);
  if (path) advanceUnitAlongPath(next, unit, path, stepContext, moveBonus);

  // Reached it this turn → slip inside and begin scavenging.
  if (entrance && distanceInMeters(next.position, entrance) <= POI_ENTER_RANGE_METERS) {
    log.push({ type: "entered-building", unitId: unit.id, buildingId, coordinate: [...next.position] });
    next.entryCoordinate = [...next.position];
    next.status = "scavenging";
    next.scavengeBuildingId = buildingId;
    next.scavengeMode = order.mode ?? "grab-one";
    if (Array.isArray(order.poiCoordinate)) next.position = [...order.poiCoordinate]; // inside, for the visual
  }
  return next;
}

// Legacy single-POI scavenge: approach the entrance, slip inside and scavenge
// for a turn, then step back out with a fresh rollSearchItems. Preserved verbatim
// for the headless auto-run path and its tests until they migrate to the
// per-building container model above. No noise for now.
function applyLegacyScavengeOrder(unit, next, order, stepContext, log, moveBonus = 0) {
  // Already searched (run finished) — idempotent no-op until the order is cleared.
  if (unit.scavenged && unit.status !== "scavenging") return next;

  if (unit.status === "scavenging") {
    const turnsLeft = (unit.scavengeTurnsLeft ?? SCAVENGE_TURNS) - 1;
    if (turnsLeft > 0) return { ...next, status: "scavenging", scavengeTurnsLeft: turnsLeft };
    // Done: roll the supplies, keep what fits, exit to where we entered. Anything
    // over carry capacity stays behind on the shelf — better to know than to
    // silently vanish.
    const exit = Array.isArray(unit.entryCoordinate) ? [...unit.entryCoordinate] : [...unit.position];
    const rolledItemIds = rollSearchItems({ rng: stepContext.rng ?? Math.random });
    const capacity = remainingCapacity(unit);
    const keptItemIds = rolledItemIds.slice(0, capacity);
    const leftBehindItemIds = rolledItemIds.slice(capacity);
    log.push({ type: "scavenged", unitId: unit.id, coordinate: [...exit], items: keptItemIds });
    if (leftBehindItemIds.length) {
      log.push({ type: "items-left-behind", unitId: unit.id, items: leftBehindItemIds, reason: "carry-capacity" });
    }
    return {
      ...next,
      status: "active",
      scavenged: true,
      inventory: [...(unit.inventory || []), ...keptItemIds],
      position: exit,
      scavengeTurnsLeft: 0,
      entryCoordinate: null,
      order: null,
    };
  }

  // Approaching the entrance.
  const path = pathFromOrder(unit, order);
  const entrance = Array.isArray(order.target) ? order.target : (path ? path[path.length - 1] : null);
  if (path) advanceUnitAlongPath(next, unit, path, stepContext, moveBonus);

  // Reached the entrance this turn → step inside and begin scavenging.
  if (entrance && distanceInMeters(next.position, entrance) <= POI_ENTER_RANGE_METERS) {
    log.push({ type: "entered-poi", unitId: unit.id, coordinate: [...next.position] });
    next.entryCoordinate = [...next.position];
    next.status = "scavenging";
    next.scavengeTurnsLeft = Math.max(1, SCAVENGE_TURNS + (unit.scavengeTurnsDelta ?? 0));
    if (Array.isArray(order.poiCoordinate)) next.position = [...order.poiCoordinate]; // inside, for the visual
  }
  return next;
}
